import { Navigate } from "react-router-dom";
import { AppHeader } from "../components/AppHeader";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can } from "@family/shared";

interface Item {
  title: string;
  detail: string;
  /** Path under /release-notes/ in public; shown below the item. */
  image?: string;
  imageAlt?: string;
}
interface Day {
  date: string;
  label: string;
  items: Item[];
}

/** Newest first. Written for moderators: what changed for members, and
 *  what changed for you. Admin-only features (Settings, Stats, deploys)
 *  are left out on purpose. Screenshots are renders of the shipped UI. */
const DAYS: Day[] = [
  {
    date: "2026-10-22",
    label: "Thursday 22 October",
    items: [
      {
        title: "Install the site as an app",
        detail:
          "The home page now offers to put the site on your phone's home screen, where it opens like an app with its own icon. On Android (and in Chrome or Edge on a computer) it's one tap on \"Install app\". On iPhone, which has no install button, the card shows the steps instead: Share, then \"Add to Home Screen\". The card disappears once the app is installed, and \"Not now\" hides it for 30 days.",
      },
      {
        title: "A reminder to turn on notifications",
        detail:
          "Once the app is installed, members who have never turned notifications on see a \"Turn on notifications\" card with the same switch as on My profile. On iPhone notifications only work from the home-screen app, which is why the install card comes first.",
      },
    ],
  },
  {
    date: "2026-10-12",
    label: "Monday 12 October",
    items: [
      {
        title: "Events without a date",
        detail:
          "An event no longer needs a date to be saved. Tick \"Date not set yet\" (handy for a postponed event, or one still being planned) and it shows as \"Date TBA\", stays under Upcoming, and members can still RSVP to show interest. Add the real date by editing the event once it's decided.",
      },
      {
        title: "Let the family vote on a date (optional)",
        detail:
          "When the date isn't set, the organiser can suggest a few dates. Members tick every date they could make on the event page and see who else can come. The organiser then presses \"Pick this date\", which sets the date and closes the vote. Leave the suggestions empty and there's simply no vote.",
        image: "oct12-date-poll.png",
        imageAlt: "Help pick a date card with three dates, vote counts and Pick this date buttons",
      },
    ],
  },
  {
    date: "2026-09-20",
    label: "Sunday 20 September",
    items: [
      {
        title: "Add person: link them to the tree in the same step",
        detail:
          "Adding someone used to need a second, easy-to-miss \"+ Add relationship\" step, so people were created and never appeared in the tree. The form now has a \"How are they related?\" block right under the name (child of, parent of, married to or sibling of, plus a search of the tree), and the button becomes \"Add and link\". It stays optional, but if you leave it empty the form tells you the person won't show in the tree.",
      },
      {
        title: "Say what each person is",
        detail:
          "On Tree audit you can now give each person a standing: Family, Married in, Related by marriage (an in-law's own relative, like a grandmother's father), Friend of the family, or Not a relative (people who appear in photos or stories). Only the last three keep someone out of the tree count. Their page still exists and says what they are. Choosing Family does not link anyone: people marked Family or Married in stay on the review list until they are actually connected.",
      },
      {
        title: "Tagging a new name in a photo asks who they are",
        detail:
          "Typing a name that isn't in the tree used to quietly add a new tree person. Now you choose: part of the family (added and listed on Tree audit so someone can connect them), friend of the family, or not family. The last two are never counted in the tree.",
      },
      {
        title: "Siblings list is complete",
        detail:
          "A person's Siblings now shows everyone who shares at least one parent with them, oldest first, not only the siblings somebody linked by hand. Half-siblings are listed like everyone else. Fix a parent and every sibling list follows.",
      },
      {
        title: "Family AI button only for people who have it",
        detail:
          "On phones the AI button in the bottom bar showed for everyone but did nothing for members who aren't in the AI beta. It now only appears for those who are.",
      },
    ],
  },
  {
    date: "2026-09-19",
    label: "Saturday 19 September",
    items: [
      {
        title: "Tree audit page",
        detail:
          "For moderators and admins: open it from the menu or the link on the Family tree page. It sorts everyone into in the family, married in, outside the family, not connected and not linked, shows when each person was added and by whom (recorded from now on), and has a Recently added tab. Not-linked people get a \"Link in tree\" button that opens the tree ready to connect them.",
      },
      {
        title: "Members of one family can sign up for another",
        detail:
          "Someone already in one family who signs in on another family's site now shows up as a pending sign-up there, and that family's moderators are notified. Before, the request never arrived. Their branch and parents' answers are kept separately for each family.",
      },
    ],
  },
  {
    date: "2026-09-10",
    label: "Thursday 10 September",
    items: [
      {
        title: "Sign-up asks for the branch and parents",
        detail:
          "New members must now pick their branch and give their mother's and father's names before finishing, so pending cards come with what you need to place them. An \"I don't know these names\" box lets people who genuinely don't know skip the names.",
      },
      {
        title: "Half-finished sign-ups are marked",
        detail:
          "A pending card for someone who signed in but never filled in the form now reads \"⏳ Hasn't finished signing up\", so it's clearly different from a real applicant.",
      },
    ],
  },
  {
    date: "2026-09-08",
    label: "Tuesday 8 September",
    items: [
      {
        title: "behailay.com and adhanom.com install as apps",
        detail:
          "Both sites can now be added to a phone's home screen as a proper app with their own icon, which is also what makes notifications work on iPhone. Anyone who added behailay.com earlier should remove it and use \"Add to Home Screen\" again.",
      },
    ],
  },
  {
    date: "2026-09-07",
    label: "Monday 7 September",
    items: [
      {
        title: "Search follows the page you're on",
        detail:
          "The magnifier in the phone header now searches what you're looking at: members on the Members page (new \"Find a member…\" box), people on the Family tree, posts everywhere else.",
      },
    ],
  },
  {
    date: "2026-09-06",
    label: "Sunday 6 September",
    items: [
      {
        title: "Push notifications actually work now",
        detail:
          "They had never reached anyone, on any device, because of a server-side configuration problem. Fixed this morning: a test sign-up delivered a notification and an app-icon badge to an iPhone home-screen app.",
      },
      {
        title: "Notifications switch in the menu and on My profile",
        detail:
          "The control used to hide under the phone's bottom bar. It's now the last item of the menu list and a box on My profile, and it's a real on/off switch. Opening the app also quietly re-registers the device, so a re-added home-screen icon keeps working.",
        image: "sep6-notifications.png",
        imageAlt: "Notifications on this device box with the on/off switch",
      },
      {
        title: "How to + Release notes pages",
        detail:
          "How to (everyone) walks through the everyday things in the order of the family video. Release notes (this page) is for moderators and admins.",
      },
    ],
  },
  {
    date: "2026-09-05",
    label: "Saturday 5 September",
    items: [
      {
        title: "Home page: welcome hero only for newcomers",
        detail:
          "The big welcome text with the six navigation cards now shows for the first 7 days after onboarding. After that the home page opens straight on the stats bar, Recently added, the composer and the feed. \"About this site\" in the menu brings the hero back on demand.",
        image: "sep5-home.png",
        imageAlt: "Compact stats bar with cover thumbnail and registered count",
      },
      {
        title: "Stats bar shows registered members",
        detail:
          "The bold number is approved site accounts. The tree figures are relabelled \"in the tree\" so the two aren't confused. On phones the stats collapse to a two-line strip.",
      },
      {
        title: "Sign-ups tell us their branch and parents",
        detail:
          "Onboarding asks which branch of the family they descend from (the founder's children), their mother's and father's names, and a relative already on the site. All optional, free text, no tree lookup before approval. Existing members can fill the same block in on My profile.",
        image: "sep5-onboarding.png",
        imageAlt: "Onboarding block with branch picker and parent name fields",
      },
      {
        title: "Pending cards suggest tree matches",
        detail:
          "Each pending sign-up shows what they told us and \"Suggested from the tree\" chips with a reason each (\"child of Yonas Kidane\"), ranked branch-first. Pick a chip and \"Add & link\" approves and links in one go. The manual search stays below.",
        image: "sep5-pending.png",
        imageAlt: "Pending card with facts box and suggested chips",
      },
      {
        title: "Branch moderators get told first",
        detail:
          "If you look after a branch, a sign-up naming that branch sends you an email and a push with the applicant's parent names, so the person best placed to vet them sees it right away.",
      },
      {
        title: "Find a person in the tree",
        detail:
          "A search box next to Expand all. Partial words work (\"yon kid\"). Picking a match recenters the tree on that person, rings their card in the family colour and zooms in.",
        image: "sep5-tree.png",
        imageAlt: "Tree recentred on the searched person with a ring",
      },
    ],
  },
  {
    date: "2026-09-02",
    label: "Wednesday 2 September",
    items: [
      {
        title: "Pending sign-ups at the top of Members, with badges",
        detail:
          "Pending people are highlighted first on the Members page, and the menu shows a count next to Members and on the ☰ button so nobody waits unnoticed.",
      },
      {
        title: "Approve and link to the tree in one step",
        detail:
          "Pending cards pre-search the tree with the sign-up name and show match chips; \"Add & link\" approves and links. The default role for new members is contributor.",
      },
    ],
  },
  {
    date: "2026-09-01",
    label: "Tuesday 1 September",
    items: [
      {
        title: "Moderators can link members to their tree person",
        detail: "A new members:link-tree permission, with Change / Unlink buttons on member cards.",
      },
      {
        title: "Members can edit their own tree person",
        detail: "Contributors get an Edit button on their own person page (no Delete).",
      },
      {
        title: "Person cover photos keep their shape",
        detail: "No more stretched crops on wide screens.",
      },
    ],
  },
  {
    date: "2026-08-31",
    label: "Monday 31 August",
    items: [
      {
        title: "Moderator role can approve members",
        detail:
          "Moderators see the Members page and approve pending users (as contributor or viewer). Who approved whom, and when, is recorded.",
      },
      {
        title: "Emails to new members",
        detail:
          "A \"thanks for signing up\" email when someone finishes onboarding, and a welcome email when they're approved.",
      },
      {
        title: "Install as an app",
        detail:
          "The site can be added to a phone's home screen and opens like an app, with notifications and an app-icon badge for moderators.",
      },
      {
        title: "Small layout fixes",
        detail:
          "Recently added no longer widens the page on phones; more space between + New post and the search bar; the role-preview banner has its own row.",
      },
    ],
  },
  {
    date: "2026-08-30",
    label: "Sunday 30 August",
    items: [
      {
        title: "Faster photos",
        detail:
          "Server-generated thumbnails and browser caching. Full-resolution originals are preserved and a display copy is served.",
      },
      {
        title: "Nicknames",
        detail: "A person can have the nickname they're actually known by; it's searchable and shown wherever they're named.",
      },
      {
        title: "Pending list shows only our own sign-ups",
        detail: "People who signed up on another family's site no longer appear in this family's pending list.",
      },
    ],
  },
];

export function ReleaseNotesPage() {
  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);

  if (meQuery.isLoading) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }
  if (!primary || !can(primary.role, "members:approve")) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <AppHeader />
      <main className="page stack-lg release-notes">
        <div>
          <h1>Release notes</h1>
          <p className="text-muted" style={{ margin: 0 }}>
            What changed on the site, day by day. For moderators and admins.
          </p>
        </div>

        {DAYS.map((day) => (
          <section key={day.date} className="stack">
            <h2>{day.label}</h2>
            {day.items.map((item) => (
              <article key={item.title} className="card stack" style={{ padding: "14px 16px", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: "1.05rem" }}>{item.title}</h3>
                <p style={{ margin: 0 }}>{item.detail}</p>
                {item.image && (
                  <img
                    src={`/release-notes/${item.image}`}
                    alt={item.imageAlt ?? item.title}
                    loading="lazy"
                    style={{
                      width: "100%",
                      maxWidth: 900,
                      border: "1px solid var(--c-border)",
                      borderRadius: 8,
                      marginTop: 4,
                    }}
                  />
                )}
              </article>
            ))}
          </section>
        ))}
      </main>
    </>
  );
}
